import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Button,
  Divider,
  FileInput,
  Icon,
  IconButton,
  TextInput,
  Typography,
  Modal,
  ModalProps,
  SidePanel,
  Loading,
  CheckboxInput,
} from "@/components/atoms";
import { DataTable, EmptyState } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useProfile } from "@/hooks";
import {
  useAddJobAttachmentMutation,
  useLazyGetJobAttachmentQuery,
  useLazyGetJobAttachmentsQuery,
  useRemoveJobAttachmentMutation,
} from "@/redux/apis/booking/endpoints/actions";
import { BookingAttachment } from "@/redux/slices/booking/types";
import { addToast } from "@/utils";
import { useSelectedBooking } from "../../hooks/useSelectedBooking";
import { bookingAttachmentColumns } from "../fixtures";
import { BookingAttachmentData } from "../types";
import { isEmpty } from "ramda";

interface JobAttachmentsProps {
  open: boolean;
  onClose: () => void;
}

const schema = z.object({
  permission: z.array(z.string()),
  description: z.string().optional(),
});
type FormData = z.infer<typeof schema>;

export const JobAttachments = ({ open, onClose }: JobAttachmentsProps) => {
  const { id: bookingId, jobNumber } = useSelectedBooking();
  const [getAttachments, { isFetching, data: attachments }] = useLazyGetJobAttachmentsQuery();
  const [getAttachment] = useLazyGetJobAttachmentQuery();
  const [add, { isLoading: isAdding }] = useAddJobAttachmentMutation();
  const [remove, { isLoading: isRemoving }] = useRemoveJobAttachmentMutation();
  const [pendingDeleteAttachmentId, setPendingDeleteAttachmentId] = useState<string | null>(null);

  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, watch, reset } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      permission: ["driver"],
      description: "",
    },
  });
  const watchPermission = watch("permission");
  const isDisabled = !(selectedFiles?.length && !isEmpty(watchPermission)) || isAdding;

  useEffect(() => {
    if (open) getAttachments(bookingId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const downloadAttachment = (attachmentId: string) => {
    getAttachment({ bookingId, documentId: attachmentId })
      .unwrap()
      .then((res) => {
        saveAs(res.link);
        addToast("success", "Starting download....");
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const removeAttachment = (attachmentId: string) => {
    setPendingDeleteAttachmentId(attachmentId);
  };

  const handleDeleteAttachment = () => {
    if (!pendingDeleteAttachmentId) {
      console.error("No selected attachment to delete");
      return;
    }

    remove({ bookingId, documentId: pendingDeleteAttachmentId })
      .unwrap()
      .then(() => {
        addToast("success", "Attachment removed successfully");
        setPendingDeleteAttachmentId(null);
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const handleRemoveUpload = () => {
    setSelectedFiles(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
    }
  };

  const onSubmit = (body: FormData) => {
    const { description, permission } = body;

    if (isDisabled) {
      addToast("danger", "Please choose a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file_upload", selectedFiles[0]);
    formData.append("description", description ?? "");
    formData.append("permission", permission.includes("driver") ? "driver" : "admin");
    add({ bookingId, body: formData })
      .unwrap()
      .then(() => {
        addToast("success", "Attachment uploaded successfully");
        setSelectedFiles(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
          fileInputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
        }
        reset();
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const onConfirmModalClose = () => setPendingDeleteAttachmentId(null);

  const renderModalButtons = () => (
    <>
      <Button variant="secondary" onClick={() => setPendingDeleteAttachmentId(null)} className="px-8">
        Cancel
      </Button>
      <Button variant="primary" onClick={handleDeleteAttachment} className="bg-danger px-8 hover:bg-danger-dark">
        Delete
      </Button>
    </>
  );

  return (
    <>
      <SidePanel open={open} onClose={onClose} width={800} focused>
        <section className="flex items-center justify-between p-5">
          <div className="flex items-center gap-2">
            <Icon name="Paperclip2" size="lg" />
            <Typography variant="title">Attachments ({jobNumber})</Typography>
          </div>
          <div className="ml-3 flex h-7 items-center">
            <IconButton iconName="ArrowRight" onClick={onClose} className="-mr-2" variant="custom" />
          </div>
        </section>

        <section className="flex h-full flex-col p-5">
          {isFetching ? (
            <Loading />
          ) : (
            <>
              <form className="rounded-lg bg-neutral-surface-gray border border-neutral-mid-gray p-5" onSubmit={handleSubmit(onSubmit)}>
                <div className="flex items-center justify-between">
                  <FileInput
                    ref={fileInputRef}
                    className="mr-2"
                    multiple={false}
                    onChange={(e) => setSelectedFiles(e.target.files && e.target.files.length ? e.target.files : null)}
                  />
                  {selectedFiles?.length ? (
                    <IconButton
                      isCustomIcon
                      iconName="close"
                      iconSize="md"
                      size="md"
                      variant="custom"
                      onClick={handleRemoveUpload}
                    />
                  ) : null}
                </div>

                {selectedFiles?.length ? (
                  <>
                    <Divider className="my-3" />
                    <div className="flex flex-col">
                      <div className="flex flex-col gap-y-4">
                        <Typography variant="action">Who may access this attachment?</Typography>
                        <CheckboxInput
                          label="Admin"
                          value="admin"
                          variant="check"
                          {...register("permission")}
                        />
                        <CheckboxInput
                          label="Driver"
                          value="driver"
                          variant="check"
                          {...register("permission")}
                        />
                      </div>

                      <div className="flex flex-col mt-5">
                        <Typography variant="action">Attachment Description</Typography>
                        <TextInput
                          size="md"
                          className="mt-1"
                          placeholder="Type here"
                          autoFocus
                          {...register("description")}
                        />
                      </div>

                      <div className="flex mt-5">
                        <Button type="submit" size="md" startIcon="DocumentUpload" disabled={isDisabled}>
                          Upload Attachment File
                        </Button>
                      </div>
                    </div>
                  </>
                ) : null}
              </form>

              {!attachments || attachments.length === 0 ? (
                <EmptyState
                  title="Nothing here… yet"
                  description="No files have been uploaded for this job."
                  className="rounded-lg bg-neutral-surface-gray border border-neutral-mid-gray mt-4"
                />
              ) : (
                <JobAttachmentsTable
                  isLoading={isAdding}
                  attachments={attachments}
                  downloadAttachment={downloadAttachment}
                  removeAttachment={removeAttachment}
                />
              )}
            </>
          )}

          <ConfirmRemoveAttachment open={!!pendingDeleteAttachmentId} onClose={onConfirmModalClose} renderButtons={renderModalButtons}>
            {isRemoving && <Loading />}
            <Typography>
              Are you sure you want to remove{" "}
              <span className="text-info">{attachments?.find((i) => i.id === pendingDeleteAttachmentId)?.filename} </span>
              from the Job ({jobNumber}) Attachments?
            </Typography>
          </ConfirmRemoveAttachment>
        </section>
      </SidePanel>
    </>
  );
};

interface JobAttachmentsTableProps {
  isLoading: boolean;
  attachments: BookingAttachment[];
  downloadAttachment: (attachmentId: string) => void;
  removeAttachment: (attachmentId: string) => void;
}

const JobAttachmentsTable = ({ isLoading, attachments, downloadAttachment, removeAttachment }: JobAttachmentsTableProps) => {
  const { dateFormat, fullTimeFormat } = useProfile();
  const data: BookingAttachmentData[] = attachments.map((attachment) => ({
    id: attachment.id,
    dateTime: (
      <div className="flex flex-col">
        <Typography variant="paragraph">{format(new Date(attachment.createdAt), dateFormat)}</Typography>
        <Typography variant="small-regular" className="text-neutral-dark-gray">{format(new Date(attachment.createdAt), fullTimeFormat)}</Typography>
      </div>
    ),
    filename: (
      <div className="grid ">
        <Typography variant="paragraph">{attachment.filename}</Typography>
        <Typography variant="small-regular" className="text-neutral-dark-gray truncate">{attachment.description}</Typography>
      </div>
    ),
    permission: <Typography variant="paragraph" className="capitalize">{attachment.permission}</Typography>,
    uploader: (
      <div className="grid">
        <Typography variant="paragraph" className="truncate">{attachment.uploader.name}</Typography>
        <Typography variant="small-regular" className="text-neutral-dark-gray truncate">{attachment.uploader.email}</Typography>
      </div>
    ),
    actions: (
      <div className="flex justify-center">
        <IconButton
          onClick={() => downloadAttachment(attachment.id)}
          iconName="ImportCurve"
          iconSize="lg"
          variant="custom"
          className="p-1 text-neutral-dark-gray"
        />
        <IconButton
          onClick={() => removeAttachment(attachment.id)}
          iconName="Trash"
          iconSize="lg"
           variant="custom"
          className="p-1 text-danger"
        />
      </div>
    ),
  }));

  if (isLoading) return <Loading />;
  return <DataTable data={data} columns={bookingAttachmentColumns} className="mt-5 [&>table_td]:text-sm" />;
};

const ConfirmRemoveAttachment = ({ open, onClose, renderButtons, children }: ModalProps) => {
  return (
    <Modal open={open} onClose={onClose} title="Delete Attachment" renderButtons={renderButtons} className="w-[515px] !p-5">
      {children}
    </Modal>
  );
};
